import type { CheckboxOnChangeArg, SelectOption } from '@library/types/utils';
import type {
  LeagueProjectionAttributes,
  LeaguesList
} from '@library/types/leagues';
import type { SortTeamColumns, TeamData } from '@library/types/teams';
import { TableBody, TableHeader } from '@library/components/table';
import { Checkbox } from '@library/components/inputs';
import { GlobalFilters } from '@library/components/GlobalFilters';
import { PaginationButtons } from '@library/components/buttons';
import RowsPerPage from '@library/components/table/RowsPerPage';
import { StyledTable } from '@bo-components/common/StyledTable';
import { StyledTableRow } from '@bo-components/common/StyledTableRow';
import { TEAMS_TABLE_HEADER } from '@bo-constants/teams';
import { useCallback } from 'react';

interface TeamsTableProps {
  allItemsChecked: boolean;
  currentPage: number;
  durations: Array<LeagueProjectionAttributes>;
  handleClickAllItemsCheckbox: ({ checked }: CheckboxOnChangeArg) => void;
  handleClickCheckbox: ({
    id,
    checked
  }: {
    id: string;
    checked: boolean;
  }) => void;
  handleClickRow: (id: string) => void;
  handleSearch: (searchValue: string) => void;
  handleSelectLeague: (leagueId: string) => void;
  handleSelectPerPage: (perPageOptions: SelectOption) => void;
  isError: boolean;
  leaguesList: [] | LeaguesList;
  searchValue: string;
  selectedLeagueId?: string;
  selectedPerPage: string;
  setCurrentPage: (page: number) => void;
  setSort: (sort: SortTeamColumns) => void;
  sort: SortTeamColumns;
  teams: Array<TeamData>;
  totalPages: number;
}

export const TeamsTable = ({
  teams,
  totalPages,
  handleSelectPerPage,
  selectedPerPage,
  durations,
  handleSearch,
  handleSelectLeague,
  searchValue,
  leaguesList,
  selectedLeagueId = '',
  isError,
  allItemsChecked,
  handleClickAllItemsCheckbox,
  handleClickCheckbox,
  handleClickRow,
  currentPage,
  setCurrentPage,
  sort,
  setSort
}: TeamsTableProps) => {
  const handleSort = useCallback(
    (sortColumn: SortTeamColumns) => {
      setSort(sortColumn);
    },
    [setSort]
  );
  return (
    <div className="flex flex-col gap-4 p-6">
      <GlobalFilters
        allowPastDates
        className="grid-cols-[fit-content(100px)_minmax(18rem,25rem)_minmax(11rem,1fr)]"
        dateFormat="MMM d, yyyy"
        disabled={false}
        durations={durations}
        handleSearch={handleSearch}
        handleSelectLeague={handleSelectLeague}
        searchDisabled={false}
        searchValue={searchValue}
        leaguesList={leaguesList}
        selectedLeagueId={selectedLeagueId}
      />
      <StyledTable
        errorMessage="Error Fetching Game Types"
        isError={isError}
        noData={!teams?.length}
        noDataMessage={{
          label: "Couldn't find any Teams",
          title: 'No Teams',
          dataTestId: 'no-game-types-results'
        }}
      >
        <TableHeader
          headers={TEAMS_TABLE_HEADER}
          sort={sort}
          onSort={handleSort}
        >
          <Checkbox
            id="stat-type-all-checkbox"
            checked={allItemsChecked}
            onCheckboxChange={handleClickAllItemsCheckbox}
          />
        </TableHeader>
        <TableBody>
          {teams?.map((team) => (
            <StyledTableRow
              headers={TEAMS_TABLE_HEADER}
              key={`team-${team.id}`}
              rowData={team}
              onClick={handleClickRow}
              onClickCheckbox={handleClickCheckbox}
              isAllChecked={allItemsChecked}
            />
          ))}
        </TableBody>
      </StyledTable>
      <div className="bg-soFresh-100 border-soFresh-140 sticky bottom-0 flex items-center justify-between gap-4 border-t">
        <RowsPerPage
          handleSelectPerPage={handleSelectPerPage}
          offset={173}
          placement="top-start"
          selectedPerPage={selectedPerPage}
        />
        <PaginationButtons
          currentPage={currentPage}
          handlePageChange={setCurrentPage}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
};
